import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';

/**
 * Confirmation for actions that change what other people see.
 *
 * Settings had two destructive paths with no meaningful guard: "Exclude from
 * directory" fired immediately from inside a *role* dropdown, and department
 * rename used window.confirm — which is unstyled, unscannable, and easy to
 * dismiss by reflex.
 *
 * Pass `confirmPhrase` for the genuinely irreversible ones: typing the name is
 * slow on purpose, and it makes "I clicked the wrong row" impossible.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  detail,
  confirmLabel = 'Confirm',
  confirmPhrase = null,
  tone = 'danger',
  onConfirm,
  onCancel,
}) {
  const [typed, setTyped] = useState('');
  const inputRef = useRef(null);
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!open) { setTyped(''); return; }
    // Focus the safe control, not the destructive one
    const target = confirmPhrase ? inputRef.current : cancelRef.current;
    const t = setTimeout(() => target?.focus(), 40);
    return () => clearTimeout(t);
  }, [open, confirmPhrase]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  const ready = !confirmPhrase || typed.trim() === confirmPhrase;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            className="w-full max-w-md rounded-card bg-surface border border-line shadow-e3 overflow-hidden"
          >
            <div className="flex items-start gap-3 p-4">
              <span className={`flex-none w-8 h-8 rounded-full flex items-center justify-center
                ${tone === 'danger' ? 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400'
                                    : 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400'}`}>
                <AlertTriangle size={15} />
              </span>
              <div className="min-w-0">
                <h3 id="confirm-title" className="text-[14px] font-bold text-ink leading-snug">{title}</h3>
                <p className="text-[12.5px] text-ink-2 mt-1 leading-relaxed">{body}</p>
                {detail && (
                  <p className="text-[11.5px] text-ink-3 mt-2 leading-relaxed">{detail}</p>
                )}
              </div>
            </div>

            {confirmPhrase && (
              <div className="px-4 pb-3">
                <label className="block text-[11px] text-ink-3 mb-1.5">
                  Type <b className="font-mono font-semibold text-ink-2">{confirmPhrase}</b> to continue
                </label>
                <input
                  ref={inputRef}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && ready) onConfirm?.(); }}
                  className="w-full h-8 rounded-ctl px-2.5 text-[12.5px] outline-none
                             bg-surface border border-line text-ink focus:border-accent"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            )}

            <div className="flex items-center justify-end gap-2 p-3 border-t border-line bg-surface-2">
              <button
                ref={cancelRef}
                onClick={onCancel}
                className="h-8 px-3 rounded-ctl text-[12px] font-semibold text-ink-2 hover:text-ink transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => ready && onConfirm?.()}
                disabled={!ready}
                className={`h-8 px-3 rounded-ctl text-[12px] font-semibold text-white transition-opacity
                  disabled:opacity-40 disabled:cursor-not-allowed
                  ${tone === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
              >
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
